import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { ArrowPathRoundedSquareIcon, CalendarDaysIcon, ClockIcon, DocumentCheckIcon, HeartIcon, TruckIcon } from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Import useNavigate
import Shifts from './shifts';
import ShiftBooking from './bookshifts';
import StaffNav from './managernav';
import RoleCalibration from './rolecalibration';
import HolidayApprove from './holidayapprove';
import StaffCosts from './staffcosts';
import StaffDetails from './staffdetails';
import Policies from './policies';
import Disciplinary from './disciplinarys';
import BackToWorkForm from './backtoworkform';
import StaffActions from './staffactions';
import { BellIcon, ChartBarSquareIcon, UserCircleIcon, FolderIcon } from '@heroicons/react/24/solid';
import Travel from './travel';
import Maternity from './maternity';
import Wellbeing from './Wellbeing';

const tabs = [
  { name: 'Policies', component: <Policies />, icon: CalendarDaysIcon },
  { name: 'Records', component: <StaffDetails />, icon: FolderIcon },
  { name: 'Reporting', url: '/appraisals', icon: DocumentCheckIcon }, // Pointing to a URL
  {name: 'Discipline', component: <Disciplinary />, icon: ArrowPathRoundedSquareIcon },
  { name: 'Sick Leave', url: '/backtoworkdashboard', icon: UserCircleIcon }, // Pointing to a URL
  { name: 'Wellbeing', component: <Wellbeing />, icon: HeartIcon },
  {name: 'Finances',  url: '/finances' , icon: ChartBarSquareIcon},
  {name: 'Overtime', url: '/overtime-manager', icon: ClockIcon},
  { name: 'Travel', component: <Travel />, icon: TruckIcon },
  { name: 'Maternity', component: <Maternity />, icon: BellIcon },


];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Example() {
  const location = useLocation();
  const [currentTabIndex, setCurrentTabIndex] = useState(1); // Default to the first tab index
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const requestedTab = location.state?.managementTab;
    if (!requestedTab) {
      return;
    }

    const nextIndex = tabs.findIndex((tab) => tab.name === requestedTab);
    if (nextIndex >= 0) {
      setCurrentTabIndex(nextIndex);
    }
  }, [location.state?.managementTab]);

  const handleTabChange = (index) => {
    setCurrentTabIndex(index);
  };

  return (
    <div className="w-full sm:pb-0 pb-20">
      <div className="hidden sm:block w-full">
        {/* Desktop/tablet version - horizontal tab navigation with scrollable tabs */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg shadow-lg p-1 w-full">
          <nav aria-label="Tabs" className="flex overflow-x-auto flex-nowrap space-x-1 w-full hide-scrollbar">
            {tabs.map((tab, index) => (
              <button
                key={tab.name}
                onClick={() => {
                  if (tab.url) {
                    navigate(tab.url);
                  } else {
                    handleTabChange(index);
                  }
                }}
                className={classNames(
                  currentTabIndex === index
                    ? 'bg-white text-emerald-700 shadow-md'
                    : 'text-white hover:bg-white/20',
                  'rounded-md px-4 py-3 text-sm font-medium flex items-center transition-all duration-200 whitespace-nowrap min-w-[120px] justify-center'
                )}
              >
                {tab.icon && (
                  <tab.icon 
                    className={classNames(
                      currentTabIndex === index ? 'text-emerald-600' : 'text-white',
                      'mr-2 h-5 w-5'
                    )} 
                    aria-hidden="true" 
                  />
                )}
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
        <style dangerouslySetInnerHTML={{__html: `
          .hide-scrollbar::-webkit-scrollbar {
            height: 4px;
          }
          .hide-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .hide-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.5);
            border-radius: 20px;
          }
        `}} />
      </div>

      {/* Render the content of the selected tab */}
      <div className="mt-4 w-full">
        {tabs[currentTabIndex].component && tabs[currentTabIndex].component}
        {!tabs[currentTabIndex].component && tabs[currentTabIndex].url && (
          <div className="flex justify-center items-center p-8 text-gray-500">
            <p>Navigating to {tabs[currentTabIndex].url}...</p>
          </div>
        )}
      </div>

      {/* Mobile bottom swipeable tab bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50">
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 p-2 shadow-2xl">
          <div className="overflow-x-auto flex space-x-2 hide-scrollbar px-1">
            {tabs.map((tab, index) => (
              <button
                key={`mobile-bottom-${tab.name}`}
                onClick={() => {
                  if (tab.url) {
                    navigate(tab.url);
                  } else {
                    handleTabChange(index);
                  }
                }}
                className={classNames(
                  currentTabIndex === index
                    ? 'bg-white text-emerald-700 shadow-md'
                    : 'bg-emerald-600/40 text-white hover:bg-emerald-700/70',
                  'whitespace-nowrap px-3 py-2 text-xs font-semibold rounded-full flex-shrink-0 transition-all duration-200 flex items-center min-w-[110px] justify-center'
                )}
              >
                {tab.icon && (
                  <tab.icon
                    className={classNames(
                      currentTabIndex === index ? 'text-emerald-600' : 'text-white',
                      'mr-1 h-4 w-4'
                    )}
                  />
                )}
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
